import {
  useOrganizationCreationDefaults,
  useOrganizationList,
  useSignUp,
  useUser,
} from "@clerk/react";
import { isClerkAPIResponseError } from "@clerk/react/errors";
import { useState } from "react";

import { InlineError } from "@/components/auth/inline-error";
import { PrimaryButton } from "@/components/auth/primary-button";
import { SuccessState } from "@/components/auth/success-state";
import { TextField } from "@/components/auth/text-field";
import { redirectAfterAuth } from "@/lib/auth-helpers";
import { followDecorated } from "@/lib/follow-auth";

const redirectDelay = (): number =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 400 : 1100;

export const OrganizationCreateForm = ({ eyebrow }: { eyebrow?: string }) => {
  const { user } = useUser();
  const { signUp } = useSignUp();
  const organizations = useOrganizationList();
  const defaults = useOrganizationCreationDefaults();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const first = user?.firstName ?? signUp.firstName ?? "";
  const last = user?.lastName ?? signUp.lastName ?? "";
  const personal = [first, last].filter(Boolean).join(" ").trim();
  const preview = name.trim() || personal || "Your name";
  const initial = preview.slice(0, 1).toUpperCase();
  const domainConflict =
    defaults.data?.advisory?.code === "organization_already_exists";

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!organizations.isLoaded) {
      return;
    }
    const organizationName = name.trim() || personal;
    if (!organizationName) {
      setError("Enter your first and last name, or an organization name.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      const created = await organizations.createOrganization({
        name: organizationName,
      });
      await organizations.setActive({
        navigate: ({ decorateUrl }) => {
          setDone(true);
          window.setTimeout(
            () => followDecorated(decorateUrl, redirectAfterAuth()),
            redirectDelay()
          );
        },
        organization: created.id,
      });
    } catch (err) {
      const message = isClerkAPIResponseError(err)
        ? (err.errors[0]?.longMessage ?? err.errors[0]?.message)
        : null;
      setError(message ?? "The organization could not be created.");
      setPending(false);
    }
  };

  if (done) {
    return (
      <SuccessState
        subtitle="Your account and organization are ready. Opening Anomalos Studio…"
        title="You’re all set"
      />
    );
  }

  return (
    <form className="flex flex-col gap-8" onSubmit={onSubmit}>
      <div className="flex flex-col gap-2">
        {eyebrow ? (
          <p className="mb-1 font-semibold text-coral-700 text-xs uppercase tracking-[0.12em]">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="font-semibold text-[32px] leading-[1.1] tracking-[-0.02em]">
          Name your organization
        </h1>
        <p className="text-base text-n-600 leading-[1.55]">
          Your Studio account lives inside an organization. Working on your own?
          Leave this blank and we’ll use your name.
        </p>
      </div>
      <TextField
        accessory="Optional"
        autoComplete="organization"
        id="organization-name"
        label="Organization name"
        onChange={(event) => setName(event.target.value)}
        placeholder={personal || "Your name"}
        value={name}
      />
      <div className="flex items-center gap-3.5 rounded-xl border border-n-200 bg-n-50 px-4 py-3.5">
        <div
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-navy-900 font-bold text-coral-500 text-lg"
        >
          {initial}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-semibold text-[15px]">{preview}</span>
          <span className="text-[13px] text-n-600">
            Organization · You’ll be the admin
          </span>
        </div>
      </div>
      {domainConflict ? (
        <p className="m-0 text-sm text-warning leading-[1.45]">
          An organization may already exist for this email domain.
        </p>
      ) : null}
      <InlineError message={error} />
      <PrimaryButton busy={pending || !organizations.isLoaded} type="submit">
        Create organization
      </PrimaryButton>
    </form>
  );
};

const SignUpOrganizationPage = () => (
  <OrganizationCreateForm eyebrow="Step 3 of 3 · Organization" />
);

export default SignUpOrganizationPage;
